import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const PAGE_SIZES = {
  a4: [210, 297],
  letter: [215.9, 279.4],
}

const MARGIN = 10 // mm
const LOGO_MAX_HEIGHT = 10 // mm
const HEADER_HEIGHT = 30 // mm — logo row + title + subtitle + separator + gap
const CANVAS_SCALE = 2
const CHUNK_GAP = 4 // mm
const MIN_AVAILABLE_HEIGHT = 8 // mm
const MIN_SPLIT_CHUNK_START_HEIGHT = 22 // mm
const KEEP_WITH_NEXT_MIN_HEIGHT = 36 // mm
const MIN_SAFE_SLICE_HEIGHT = MIN_AVAILABLE_HEIGHT
const PDF_POSITION_EPSILON = 0.1 // mm
const PDF_IMAGE_TYPE = 'PNG'
const PDF_IMAGE_COMPRESSION_FAST = 'FAST'
const LIGHT_THEME = 'light'
const LIGHT_BACKGROUND = '#ffffff'
const PAGE_BREAK_PROTECTED_SELECTOR = 'tr, p, li, h1, h2, h3, h4, h5, h6'
const KEEP_WITH_NEXT_TARGET_SELECTOR = 'thead tr, tbody tr, tr, li'
const UNSUPPORTED_HTML2CANVAS_COLOR_FUNCTION_PATTERN = /\b(?:color-mix|oklch|oklab|lch|lab|color|device-cmyk|hwb)\(/i
const CANVAS_COLOR_CONVERSION_SENTINEL = '#010203'
const HTML2CANVAS_COLOR_PROPERTIES = [
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'text-emphasis-color',
  'caret-color',
  'accent-color',
  'column-rule-color',
  '-webkit-text-fill-color',
  '-webkit-text-stroke-color',
  'fill',
  'stroke',
  'stop-color',
  'flood-color',
  'lighting-color',
]
const HTML2CANVAS_UNSUPPORTED_COLOR_DROP_PROPERTIES = [
  'box-shadow',
  'text-shadow',
  'background-image',
  'border-image-source',
]

const getPageSize = (pageSize) => PAGE_SIZES[pageSize] ?? PAGE_SIZES.a4

const createPdf = (pageSize) => {
  const [pageW, pageH] = getPageSize(pageSize)
  // eslint-disable-next-line new-cap
  return { pdf: new jsPDF('p', 'mm', [pageW, pageH]), pageW, pageH }
}

const getContentLayout = (pageW, pageH) => ({
  contentX: MARGIN,
  contentW: pageW - MARGIN * 2,
  firstPageContentY: MARGIN + HEADER_HEIGHT,
  firstPageContentH: pageH - (MARGIN + HEADER_HEIGHT) - MARGIN,
  otherPageContentH: pageH - MARGIN * 2,
})

const getDateString = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

const addPdfHeader = ({ pdf, pageW, contentX, contentW, title, subtitle, logo }) => {
  let logoEndY = MARGIN

  if (logo) {
    try {
      const imgProps = pdf.getImageProperties(logo)
      const ratio = imgProps.width / imgProps.height
      const logoH = Math.min(LOGO_MAX_HEIGHT, imgProps.height * (contentW / imgProps.width))
      const logoW = logoH * ratio
      pdf.addImage(logo, PDF_IMAGE_TYPE, contentX, MARGIN, logoW, logoH, undefined, 'SLOW')
      logoEndY = MARGIN + logoH
    } catch {
      // Logo failed to load; continue without it.
    }
  }

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(8)
  pdf.setTextColor(120, 120, 120)
  pdf.text(getDateString(), pageW - MARGIN, MARGIN + 4, { align: 'right' })

  const titleY = logoEndY + 4
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(13)
  pdf.setTextColor(52, 58, 64)
  pdf.text(title, contentX, titleY)

  if (subtitle) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(100, 100, 100)
    pdf.text(subtitle, contentX, titleY + 5)
  }

  const separatorY = MARGIN + HEADER_HEIGHT - 2
  pdf.setDrawColor(220, 220, 220)
  pdf.setLineWidth(0.3)
  pdf.line(contentX, separatorY, pageW - MARGIN, separatorY)
}

const applyLightModeToClone = (clonedDocument) => {
  clonedDocument.documentElement.setAttribute('data-bs-theme', LIGHT_THEME)
}

// Identifies computed CSS values containing color functions unsupported by html2canvas.
const hasUnsupportedHtml2CanvasColor = (value) =>
  typeof value === 'string' && UNSUPPORTED_HTML2CANVAS_COLOR_FUNCTION_PATTERN.test(value)

// Creates a cached canvas-based converter from modern CSS colors to rgb/rgba values.
const createUnsupportedColorConverter = (clonedDocument) => {
  const canvas = clonedDocument.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const cache = new Map()

  if (!context) return () => null

  // Converts one CSS color value, returning null when the browser cannot resolve it.
  return (value) => {
    if (cache.has(value)) return cache.get(value)

    try {
      context.clearRect(0, 0, 1, 1)
      context.fillStyle = CANVAS_COLOR_CONVERSION_SENTINEL
      context.fillStyle = value
      if (context.fillStyle === CANVAS_COLOR_CONVERSION_SENTINEL) {
        cache.set(value, null)
        return null
      }

      context.fillRect(0, 0, 1, 1)

      const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data
      const opacity = Number((alpha / 255).toFixed(3))
      const normalized = opacity >= 1 ? `rgb(${red}, ${green}, ${blue})` : `rgba(${red}, ${green}, ${blue}, ${opacity})`

      cache.set(value, normalized)
      return normalized
    } catch {
      cache.set(value, null)
      return null
    }
  }
}

// Replaces unsupported computed colors on one cloned element before capture.
const sanitizeElementUnsupportedColors = ({ element, style, convertUnsupportedColor }) => {
  HTML2CANVAS_COLOR_PROPERTIES.forEach((property) => {
    const value = style.getPropertyValue(property)
    if (!hasUnsupportedHtml2CanvasColor(value)) return

    const convertedColor = convertUnsupportedColor(value)
    if (!convertedColor) {
      throw new Error(`Unable to convert unsupported PDF color for ${property}: ${value}`)
    }

    element.style.setProperty(property, convertedColor, 'important')
  })

  HTML2CANVAS_UNSUPPORTED_COLOR_DROP_PROPERTIES.forEach((property) => {
    const value = style.getPropertyValue(property)
    if (!hasUnsupportedHtml2CanvasColor(value)) return

    element.style.setProperty(property, 'none', 'important')
  })
}

// Sanitizes every element in the cloned capture subtree that uses unsupported color functions.
const sanitizeUnsupportedColorsForHtml2Canvas = (clonedDocument, clonedElement) => {
  const clonedWindow = clonedDocument.defaultView
  const rootElement = clonedElement ?? clonedDocument.body
  if (!clonedWindow || !rootElement) return

  const convertUnsupportedColor = createUnsupportedColorConverter(clonedDocument)
  const elements = [
    ...new Set(
      [clonedDocument.documentElement, clonedDocument.body, rootElement, ...rootElement.querySelectorAll('*')].filter(
        Boolean,
      ),
    ),
  ]

  elements.forEach((element) => {
    sanitizeElementUnsupportedColors({
      element,
      style: clonedWindow.getComputedStyle(element),
      convertUnsupportedColor,
    })
  })
}

// Creates the html2canvas clone callback that applies the requested capture preparation.
const prepareCloneForCapture =
  ({ forceLightMode, sanitizeUnsupportedColors }) =>
  // Applies theme and color normalization after html2canvas clones the document.
  (clonedDocument, clonedElement) => {
    if (forceLightMode) {
      applyLightModeToClone(clonedDocument)
    }

    if (sanitizeUnsupportedColors) {
      // html2canvas 1.4.x cannot parse CSS Color 4 functions from getComputedStyle.
      // Normalize the cloned DOM so injected/theme styles cannot break PDF capture.
      sanitizeUnsupportedColorsForHtml2Canvas(clonedDocument, clonedElement)
    }
  }

// Returns a clone handler only when capture preparation is required.
const getCaptureCloneHandler = ({ forceLightMode, sanitizeUnsupportedColors }) => {
  if (!forceLightMode && !sanitizeUnsupportedColors) return undefined
  return prepareCloneForCapture({ forceLightMode, sanitizeUnsupportedColors })
}

const waitForDocumentFonts = async () => {
  if (!document.fonts?.ready) return

  try {
    await document.fonts.ready
  } catch {
    // Continue even if a browser rejects the FontFaceSet promise.
  }
}

const getCaptureWindowWidth = (rootElement) =>
  Math.max(document.documentElement.scrollWidth, rootElement.scrollWidth, rootElement.offsetWidth)

const captureElement = async ({
  element,
  rootElement = element,
  forceLightMode = false,
  sanitizeUnsupportedColors = true,
}) => {
  await waitForDocumentFonts()

  return html2canvas(element, {
    scale: CANVAS_SCALE,
    useCORS: true,
    logging: false,
    backgroundColor: forceLightMode ? LIGHT_BACKGROUND : undefined,
    windowWidth: getCaptureWindowWidth(rootElement),
    onclone: getCaptureCloneHandler({ forceLightMode, sanitizeUnsupportedColors }),
  })
}

const createCanvasSlice = ({ canvas, sourceY, sourceH }) => {
  const sliceCanvas = document.createElement('canvas')
  sliceCanvas.width = canvas.width
  sliceCanvas.height = sourceH
  sliceCanvas.getContext('2d').drawImage(canvas, 0, sourceY, canvas.width, sourceH, 0, 0, canvas.width, sourceH)
  return sliceCanvas
}

const getImageHeight = ({ canvas, imageWidth }) => (canvas.height / canvas.width) * imageWidth

const getAvailableHeight = ({ pageH, currentY }) => pageH - MARGIN - currentY

const addContentPage = (pdf) => {
  pdf.addPage()
  return MARGIN
}

const getChunkPdfLayout = ({ chunk, rootElement, contentX, contentW }) => {
  const rootRect = rootElement.getBoundingClientRect()
  const chunkRect = chunk.getBoundingClientRect()
  const rootWidth = rootRect.width || rootElement.scrollWidth || chunkRect.width || 1
  const relativeX = chunkRect.left - rootRect.left
  const imageX = contentX + (relativeX / rootWidth) * contentW
  const imageW = chunkRect.width ? (chunkRect.width / rootWidth) * contentW : contentW

  return { chunkRect, imageX, imageW }
}

const getEstimatedImageHeight = ({ chunkRect, imageW }) => {
  if (!chunkRect.width) return 0
  return (chunkRect.height / chunkRect.width) * imageW
}

const getPageBreakProtectedSelector = (pageBreakProtectedSelector) =>
  [PAGE_BREAK_PROTECTED_SELECTOR, pageBreakProtectedSelector].filter(Boolean).join(', ')

const getNodePageBreakRange = ({ node, chunkRect, imageH }) => {
  const rect = node.getBoundingClientRect()
  const top = ((rect.top - chunkRect.top) / chunkRect.height) * imageH
  const bottom = ((rect.bottom - chunkRect.top) / chunkRect.height) * imageH

  return {
    top: Math.max(0, top),
    bottom: Math.min(imageH, bottom),
  }
}

const isValidPageBreakRange = ({ top, bottom }, imageH) =>
  bottom - top > PDF_POSITION_EPSILON && bottom > 0 && top < imageH

const getNextVisibleElementSibling = (node) => {
  let sibling = node.nextElementSibling

  while (sibling) {
    const rect = sibling.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return sibling
    sibling = sibling.nextElementSibling
  }

  return null
}

const getKeepWithNextTargetNode = (node) => {
  const nextSibling = getNextVisibleElementSibling(node)
  if (!nextSibling) return node

  return nextSibling.querySelector(KEEP_WITH_NEXT_TARGET_SELECTOR) ?? nextSibling
}

const getKeepWithNextPageBreakRange = ({ node, chunkRect, imageH }) => {
  const range = getNodePageBreakRange({ node, chunkRect, imageH })
  const targetRange = getNodePageBreakRange({ node: getKeepWithNextTargetNode(node), chunkRect, imageH })

  return {
    top: range.top,
    bottom: Math.max(range.bottom, targetRange.bottom),
  }
}

const getProtectedPageBreakRanges = ({ chunk, imageH, pageBreakProtectedSelector, keepWithNextSelector }) => {
  const chunkRect = chunk.getBoundingClientRect()
  if (!chunkRect.height) return []

  const protectedSelector = getPageBreakProtectedSelector(pageBreakProtectedSelector)
  const protectedRanges = Array.from(chunk.querySelectorAll(protectedSelector)).map((node) =>
    getNodePageBreakRange({ node, chunkRect, imageH }),
  )
  const keepWithNextRanges = keepWithNextSelector
    ? Array.from(chunk.querySelectorAll(keepWithNextSelector)).map((node) =>
        getKeepWithNextPageBreakRange({ node, chunkRect, imageH }),
      )
    : []

  return [...protectedRanges, ...keepWithNextRanges]
    .filter((range) => isValidPageBreakRange(range, imageH))
    .sort((first, second) => first.top - second.top)
}

const getSafeSliceHeight = ({ protectedRanges, sourcePdfY, desiredSliceH, remainingH }) => {
  if (desiredSliceH >= remainingH - PDF_POSITION_EPSILON) return desiredSliceH

  const desiredEndY = sourcePdfY + desiredSliceH
  const blockingRange = protectedRanges.find(
    ({ top, bottom }) => top + PDF_POSITION_EPSILON < desiredEndY && bottom - PDF_POSITION_EPSILON > desiredEndY,
  )

  if (!blockingRange) return desiredSliceH
  return Math.max(0, blockingRange.top - sourcePdfY)
}

const addCanvasToPdf = ({ pdf, canvas, pageH, imageX, imageW, currentY, protectedRanges = [] }) => {
  const imgH = getImageHeight({ canvas, imageWidth: imageW })
  let remainingH = imgH
  let sourcePdfY = 0
  let nextY = currentY

  while (remainingH > PDF_POSITION_EPSILON) {
    let availableH = getAvailableHeight({ pageH, currentY: nextY })
    if (availableH < MIN_AVAILABLE_HEIGHT) {
      nextY = addContentPage(pdf)
      availableH = getAvailableHeight({ pageH, currentY: nextY })
    }

    const desiredSliceH = Math.min(remainingH, availableH)
    let slicePdfH = getSafeSliceHeight({ protectedRanges, sourcePdfY, desiredSliceH, remainingH })
    const shouldMoveSliceToNextPage = slicePdfH < MIN_SAFE_SLICE_HEIGHT && desiredSliceH < remainingH && nextY > MARGIN

    if (shouldMoveSliceToNextPage) {
      nextY = addContentPage(pdf)
    } else {
      if (slicePdfH < MIN_SAFE_SLICE_HEIGHT) {
        slicePdfH = desiredSliceH
      }

      const sourceY = Math.round((sourcePdfY / imgH) * canvas.height)
      const remainingCanvasH = canvas.height - sourceY
      if (remainingCanvasH <= 0) {
        remainingH = 0
        break
      }

      const sourceH = Math.max(1, Math.min(remainingCanvasH, Math.round((slicePdfH / imgH) * canvas.height)))
      const imageCanvas =
        sourceY === 0 && sourceH >= canvas.height ? canvas : createCanvasSlice({ canvas, sourceY, sourceH })

      pdf.addImage(
        imageCanvas.toDataURL('image/png'),
        PDF_IMAGE_TYPE,
        imageX,
        nextY,
        imageW,
        slicePdfH,
        undefined,
        PDF_IMAGE_COMPRESSION_FAST,
      )

      remainingH -= slicePdfH
      sourcePdfY += slicePdfH
      nextY += slicePdfH

      if (remainingH < PDF_POSITION_EPSILON) {
        remainingH = 0
      }

      if (remainingH > 0) {
        nextY = addContentPage(pdf)
      }
    }
  }

  return { nextY, imgH }
}

const getChunkElements = ({ element, chunkSelector }) => {
  if (!chunkSelector) return [element]

  const chunks = Array.from(element.querySelectorAll(chunkSelector)).filter(
    (chunk) => chunk.scrollHeight > 0 && chunk.scrollWidth > 0,
  )
  return chunks.length > 0 ? chunks : [element]
}

const shouldKeepChunkWithNext = ({ chunk, keepWithNextSelector }) =>
  Boolean(keepWithNextSelector && chunk.matches(keepWithNextSelector))

const getKeepWithNextHeight = ({ chunks, index, rootElement, contentX, contentW, otherPageContentH }) => {
  const nextChunk = chunks[index + 1]
  if (!nextChunk) return 0

  const { chunkRect, imageW } = getChunkPdfLayout({ chunk: nextChunk, rootElement, contentX, contentW })
  const nextImageH = getEstimatedImageHeight({ chunkRect, imageW })
  if (nextImageH <= otherPageContentH) return nextImageH

  return Math.min(nextImageH, KEEP_WITH_NEXT_MIN_HEIGHT)
}

const shouldStartChunkOnNewPage = ({ imgH, availableH, pageHasContent, keepWithNextH, otherPageContentH }) => {
  if (!pageHasContent) return false

  if (keepWithNextH > 0 && imgH + keepWithNextH > availableH && imgH + keepWithNextH <= otherPageContentH) {
    return true
  }

  if (imgH <= availableH) return false

  return availableH < MIN_SPLIT_CHUNK_START_HEIGHT
}

/**
 * Captures a DOM element and downloads it as a multi-page PDF.
 *
 * @param {object} options
 * @param {HTMLElement} options.element   - The DOM element to capture
 * @param {string}      options.filename  - Download filename without extension
 * @param {string}      options.title     - Bold report title shown in the header
 * @param {string}      [options.subtitle] - Lighter subtitle (e.g. customer name)
 * @param {string}      [options.logo]    - Base64 data URL or src URL for a logo image
 * @param {'a4'|'letter'} [options.pageSize='a4']
 */
export async function generatePagePDF({ element, filename, title, subtitle, logo, pageSize = 'a4' }) {
  const canvas = await captureElement({ element })

  const { pdf, pageW, pageH } = createPdf(pageSize)

  const { contentX, contentW, firstPageContentY, firstPageContentH, otherPageContentH } = getContentLayout(pageW, pageH)

  // --- Header ---

  addPdfHeader({ pdf, pageW, contentX, contentW, title, subtitle, logo })

  // --- Content ---

  // Scale the captured canvas to fit the PDF content width
  const imgW = contentW
  const imgH = (canvas.height / canvas.width) * imgW

  let remainingH = imgH
  let sourceY = 0
  let isFirstPage = true

  while (remainingH > 0) {
    const availableH = isFirstPage ? firstPageContentH : otherPageContentH
    const destY = isFirstPage ? firstPageContentY : MARGIN
    const sliceH = Math.min(remainingH, availableH)

    // Convert PDF-space slice coordinates back to canvas pixel coordinates
    const canvasSliceY = Math.round((sourceY / imgH) * canvas.height)
    const canvasSliceH = Math.round((sliceH / imgH) * canvas.height)

    const sliceCanvas = document.createElement('canvas')
    sliceCanvas.width = canvas.width
    sliceCanvas.height = canvasSliceH
    sliceCanvas.getContext('2d').drawImage(canvas, 0, -canvasSliceY)

    pdf.addImage(
      sliceCanvas.toDataURL('image/png'),
      PDF_IMAGE_TYPE,
      contentX,
      destY,
      imgW,
      sliceH,
      undefined,
      PDF_IMAGE_COMPRESSION_FAST,
    )

    remainingH -= sliceH
    sourceY += sliceH
    isFirstPage = false

    if (remainingH > 0) {
      pdf.addPage()
    }
  }

  pdf.save(`${filename}.pdf`)
}

/**
 * Captures multiple DOM chunks and downloads them as one multi-page PDF.
 * This avoids creating one very tall browser canvas for expanded reports.
 *
 * @param {object} options
 * @param {HTMLElement} options.element
 * @param {string} options.chunkSelector
 * @param {string} [options.keepWithNextSelector]
 * @param {string} [options.pageBreakProtectedSelector]
 * @param {string} options.filename
 * @param {string} options.title
 * @param {string} [options.subtitle]
 * @param {string} [options.logo]
 * @param {'a4'|'letter'} [options.pageSize='a4']
 * @param {boolean} [options.forceLightMode=true]
 */
export async function generateChunkedPagePDF({
  element,
  chunkSelector,
  keepWithNextSelector,
  pageBreakProtectedSelector,
  filename,
  title,
  subtitle,
  logo,
  pageSize = 'a4',
  forceLightMode = true,
}) {
  const { pdf, pageW, pageH } = createPdf(pageSize)
  const { contentX, contentW, firstPageContentY, otherPageContentH } = getContentLayout(pageW, pageH)
  const chunks = getChunkElements({ element, chunkSelector })

  addPdfHeader({ pdf, pageW, contentX, contentW, title, subtitle, logo })

  let currentY = firstPageContentY
  let pageHasContent = false

  for (let index = 0; index < chunks.length; index += 1) {
    const canvas = await captureElement({ element: chunks[index], rootElement: element, forceLightMode }) // eslint-disable-line no-await-in-loop
    const { imageX, imageW } = getChunkPdfLayout({ chunk: chunks[index], rootElement: element, contentX, contentW })
    const imgH = getImageHeight({ canvas, imageWidth: imageW })
    const availableH = getAvailableHeight({ pageH, currentY })
    const keepWithNextH = shouldKeepChunkWithNext({ chunk: chunks[index], keepWithNextSelector })
      ? getKeepWithNextHeight({ chunks, index, rootElement: element, contentX, contentW, otherPageContentH })
      : 0

    if (shouldStartChunkOnNewPage({ imgH, availableH, pageHasContent, keepWithNextH, otherPageContentH })) {
      currentY = addContentPage(pdf)
      pageHasContent = false
    }

    const protectedRanges = getProtectedPageBreakRanges({
      chunk: chunks[index],
      imageH: imgH,
      keepWithNextSelector,
      pageBreakProtectedSelector,
    })
    const result = addCanvasToPdf({ pdf, canvas, pageH, imageX, imageW, currentY, protectedRanges })
    currentY = result.nextY
    pageHasContent = true

    const hasMoreChunks = index < chunks.length - 1
    if (hasMoreChunks) {
      if (getAvailableHeight({ pageH, currentY }) > CHUNK_GAP + MIN_AVAILABLE_HEIGHT) {
        currentY += CHUNK_GAP
      } else {
        currentY = addContentPage(pdf)
        pageHasContent = false
      }
    }
  }

  pdf.save(`${filename}.pdf`)
}
